import React, { useEffect, useRef } from "react";
import { Search } from "lucide-react";

import { WishlistStrip } from "@/components/WishlistStrip";
import { buildWishlistSearchString } from "@/lib/wishlistSearchString";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { hapticMedium } from "@/lib/haptics";
import { useToast } from "@/hooks/use-toast";
import type { WishlistEntry } from "@/lib/wishlistStore";

/**
 * The friend's "What are you chasing?" list, rendered in the four-then-three
 * grid the Wish List screen uses.
 *
 * Press-and-hold the card (450ms) — or tap the button underneath — to copy ONE
 * Pokémon GO search string covering every species they want that you can still
 * trade, e.g. `25,455,578,34,37,28&!traded`. Paste it in-game to see which of
 * your copies could go their way. See lib/wishlistSearchString.ts.
 *
 * Extracted from Compare so the Compare Dex page carries it verbatim rather
 * than growing a second, subtly different copy of the press-and-hold wiring.
 */
export function FriendWishlistBlock({
  friendName,
  friendHue,
  entries,
  isLoading,
  className = "v2-neon-card-soft p-4 mt-4",
}: {
  friendName: string;
  friendHue?: number;
  entries: WishlistEntry[];
  isLoading: boolean;
  className?: string;
}) {
  const { toast } = useToast();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPressTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => clearPressTimer, []);

  function copyWishSearch() {
    const str = buildWishlistSearchString(entries);
    if (!str) {
      toast({
        title: "Nothing to search yet",
        description: `${friendName} hasn't added anything to chase.`,
      });
      return;
    }
    void hapticMedium();
    void copyToClipboard(str);
    toast({
      title: `Copied a search for ${friendName}'s wish list`,
      description: "Paste it into Pokémon GO's search bar to find every species they want that you can still trade.",
    });
  }

  // Only wired when the list is non-empty — a press-and-hold that copies
  // nothing is worse than no gesture at all.
  const pressProps =
    entries.length > 0
      ? {
          onPointerDown: () => {
            clearPressTimer();
            timer.current = setTimeout(copyWishSearch, 450);
          },
          onPointerUp: clearPressTimer,
          onPointerLeave: clearPressTimer,
          onPointerCancel: clearPressTimer,
          onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        }
      : {};

  return (
    <section className={className} data-testid="section-wish-lists">
      <div
        {...pressProps}
        style={
          entries.length > 0
            ? { WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }
            : undefined
        }
      >
        <WishlistStrip
          layout="grid"
          title={`${friendName} wish list`}
          entries={entries}
          emptyLabel={
            isLoading
              ? "Loading their wish list…"
              : `${friendName} hasn't added anything to chase yet.`
          }
          hue={friendHue}
          testId="wish-strip-them"
        />
      </div>
      {entries.length > 0 && (
        <button
          type="button"
          onClick={copyWishSearch}
          data-testid="button-copy-wish-search"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[11px] font-semibold hover-elevate"
          style={{
            background: "var(--surface-sunken)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-subtle)",
            minHeight: 40,
          }}
        >
          <Search className="w-3.5 h-3.5" aria-hidden />
          Copy a Pokémon GO search for their list
        </button>
      )}
    </section>
  );
}
